import { useState } from 'react';
import { CalendarClock, ClipboardList, Flag, MailCheck, Phone, X } from 'lucide-react';
import { arDunningApi } from '../../services/arDunningApi';
import { useToast } from '../ui/Toast';
import { formatMoney, todayIso } from '../../utils/arDunningFormat';
import type { ArOpenItem, ArActionType } from '../../types/arDunning';

interface Props {
    item: ArOpenItem;
    canEdit: boolean;
    onClose: () => void;
    onSaved: () => void;
    /** Optional pre-selected action type (e.g. from a row quick-action). */
    initialType?: ArActionType;
}

const TYPE_OPTIONS: { type: ArActionType; label: string; icon: typeof CalendarClock }[] = [
    { type: 'PromiseToPay', label: 'Promise to pay', icon: CalendarClock },
    { type: 'FollowUp', label: 'Follow-up task', icon: ClipboardList },
    { type: 'Call', label: 'Call', icon: Phone },
    { type: 'Dispute', label: 'Flag dispute / hold', icon: Flag },
    { type: 'ReminderSent', label: 'Mark reminded', icon: MailCheck },
];

/**
 * Center modal (right-click / quick-action) to record a dunning action on one item.
 * Records to the ar-dunning actions API — no e-mail is sent here (that is Phase 4).
 */
export function ActionModal({ item, canEdit, onClose, onSaved, initialType }: Props) {
    const { toast } = useToast();
    const suggested = item.NextLevel > 0 ? item.NextLevel : Math.max(1, item.SuggestedLevel);
    const [type, setType] = useState<ArActionType>(initialType ?? (item.NextLevel > 0 ? 'ReminderSent' : 'FollowUp'));
    const [promiseDate, setPromiseDate] = useState(todayIso());
    const [promiseAmount, setPromiseAmount] = useState<string>(item.OpenAmountFCY ? String(item.OpenAmountFCY.toFixed(2)) : '');
    const [dueAt, setDueAt] = useState(todayIso());
    const [level, setLevel] = useState<number>(suggested);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!canEdit) return;
        setSaving(true);
        try {
            const base = { entityType: 'Invoice' as const, entityRef: item.ItemKey, actionType: type };
            if (type === 'PromiseToPay') {
                if (!promiseDate) { toast('Promise date is required', 'info'); setSaving(false); return; }
                await arDunningApi.createAction({ ...base, promiseDate, promiseAmount: promiseAmount ? Number(promiseAmount) : null, notes: notes.trim() || undefined });
            } else if (type === 'FollowUp') {
                if (!dueAt) { toast('Due date is required', 'info'); setSaving(false); return; }
                await arDunningApi.createAction({ ...base, dueAt, notes: notes.trim() || undefined });
            } else if (type === 'Dispute' || type === 'Call') {
                await arDunningApi.createAction({ ...base, notes: notes.trim() || undefined });
            } else if (type === 'ReminderSent') {
                await arDunningApi.createAction({ ...base, dunningLevel: level, notes: notes.trim() || undefined });
            }
            toast('Action recorded', 'success');
            onSaved();
            onClose();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to record action', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-start justify-between">
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-slate-800">Record action</h3>
                        <p className="mt-0.5 truncate text-sm text-slate-500">
                            {item.InvoiceNo} · {item.CustomerName ?? item.CustomerCode} · {formatMoney(item.OpenAmountEUR)}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                </div>

                {/* Action type selector */}
                <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
                    {TYPE_OPTIONS.map(o => {
                        const Icon = o.icon;
                        const active = type === o.type;
                        return (
                            <button
                                key={o.type}
                                onClick={() => setType(o.type)}
                                className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-xs font-medium ${active ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                            >
                                <Icon className="h-4 w-4" />
                                {o.label}
                            </button>
                        );
                    })}
                </div>

                {/* Per-type fields */}
                {type === 'PromiseToPay' && (
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-xs font-medium text-slate-500">Promise date
                            <input type="date" value={promiseDate} onChange={e => setPromiseDate(e.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                        </label>
                        <label className="block text-xs font-medium text-slate-500">Amount ({item.Currency})
                            <input type="number" step="0.01" value={promiseAmount} onChange={e => setPromiseAmount(e.target.value)} placeholder="Optional" className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                        </label>
                        <p className="col-span-2 text-xs text-slate-400">Recording a promise pauses reminder suggestions until the promised date.</p>
                    </div>
                )}
                {type === 'FollowUp' && (
                    <label className="block text-xs font-medium text-slate-500">Follow-up date (assigned to you)
                        <input type="date" value={dueAt} onChange={e => setDueAt(e.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                    </label>
                )}
                {type === 'Dispute' && (
                    <p className="text-xs text-slate-500">Flagging a dispute/hold excludes this item from dunning until resolved. Add the reason below.</p>
                )}
                {type === 'Call' && (
                    <p className="text-xs text-slate-500">Records that the customer was called about this item. Add the outcome of the call below.</p>
                )}
                {type === 'ReminderSent' && (
                    <div>
                        <label className="block text-xs font-medium text-slate-500">Dunning level</label>
                        <div className="mt-1 flex gap-2">
                            {[1, 2, 3].map(l => (
                                <button key={l} onClick={() => setLevel(l)} className={`rounded-md border px-4 py-1.5 text-sm font-medium ${level === l ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>L{l}</button>
                            ))}
                            <span className="self-center text-xs text-slate-400">Suggested: L{suggested} · BC last: {item.BcReminderLevel ?? '–'}</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-400">Records that reminder level {level} was sent (e.g. manually). E-mail sending arrives in Phase 4.</p>
                    </div>
                )}

                <label className="mt-4 block text-xs font-medium text-slate-500">Notes
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional note…" className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                </label>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                    <button onClick={save} disabled={saving || !canEdit} className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">Save action</button>
                </div>
            </div>
        </div>
    );
}
